import { useState } from "react";
import { Edit, Trash2, Plus, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { formatCurrency } from "./utils";

/**
 * Reusable Estimate Card component for Labor, Material, and Equipment estimates
 * 
 * @param {Object} props
 * @param {string} props.title - Card title (e.g., "Labor Estimate", "Material Estimate")
 * @param {string} props.icon - Lucide icon component to display
 * @param {Array} props.estimates - Array of estimate objects (will be split into regular and change orders)
 * @param {number} props.totalEstimate - Total estimate value from job
 * @param {boolean} props.canManage - Whether user can add/edit/delete
 * @param {Function} props.onSave - Callback when saving estimates: (allEstimates) => Promise<boolean>
 * @param {Function} props.onPopulateFromBidSheet - Optional callback for populate button
 * @param {boolean} props.hasBidSheet - Whether job has bid sheets to populate from
 * @param {string} props.nameField - Field name for display (e.g., "employee_name", "material_name")
 * @param {string} props.idField - Field name for ID (e.g., "employee_id", "material_id")
 * @param {string} props.testIdPrefix - Prefix for data-testid attributes
 */
const EstimateCard = ({
  title = "Estimate",
  icon: Icon,
  estimates = [],
  totalEstimate = 0,
  canManage = false,
  onSave,
  onPopulateFromBidSheet,
  hasBidSheet = false,
  nameField = "name",
  idField = "id",
  testIdPrefix = "estimate",
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [form, setForm] = useState({
    name: "",
    amount: "",
    description: "",
  });

  // Split estimates into regular and change orders
  const regularEstimates = estimates.filter(e => !e.is_change_order);
  const changeOrderEstimates = estimates.filter(e => e.is_change_order);
  const regularTotal = regularEstimates.reduce((sum, e) => sum + (e.total || 0), 0);

  const resetForm = () => {
    setForm({ name: "", amount: "", description: "" });
    setEditingIndex(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (estimate, index) => {
    setEditingIndex(index);
    setForm({
      name: estimate[nameField] || estimate.name || "",
      amount: (estimate.total || 0).toString(),
      description: estimate.description || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const amount = parseFloat(form.amount) || 0;
    const newEstimate = {
      [idField]: `estimate_${crypto.randomUUID()}`,
      [nameField]: form.name || title.replace(" Estimate", ""),
      quantity: 1,
      unit: "lump",
      unit_price: amount,
      total: amount,
      description: form.description,
    };

    let updatedEstimates;
    if (editingIndex !== null) {
      updatedEstimates = [...regularEstimates];
      updatedEstimates[editingIndex] = newEstimate;
    } else {
      updatedEstimates = [...regularEstimates, newEstimate];
    }

    // Combine with change order items
    const allEstimates = [...updatedEstimates, ...changeOrderEstimates];
    const success = await onSave(allEstimates);
    if (success) {
      setDialogOpen(false);
      resetForm();
    }
  };

  const handleDelete = async (index) => {
    const updatedEstimates = regularEstimates.filter((_, i) => i !== index);
    const allEstimates = [...updatedEstimates, ...changeOrderEstimates];
    await onSave(allEstimates);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5" />}
          {title}
        </CardTitle>
        <div className="flex items-center gap-2">
          {canManage && hasBidSheet && onPopulateFromBidSheet && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={onPopulateFromBidSheet}
              data-testid={`populate-${testIdPrefix}-from-bid-sheet-btn`}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Populate from Bid Sheet
            </Button>
          )}
          {canManage && (
            <Button size="sm" onClick={openAddDialog} data-testid={`add-${testIdPrefix}-btn`}>
              <Plus className="w-4 h-4 mr-2" />
              Add Estimate
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-red-600 text-sm font-medium" data-testid={`${testIdPrefix}-before-tax-note`}>
          Entered Values are BEFORE Tax, Profit and Burden
        </p>
        {regularEstimates.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Description</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                {canManage && <TableHead className="w-20"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {regularEstimates.map((estimate, index) => (
                <TableRow key={estimate[idField] || estimate.id || `idx-${index}`} className="table-row">
                  <TableCell className="font-medium">
                    {estimate[nameField] || estimate.name || title.replace(" Estimate", "")}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {estimate.description || "-"}
                  </TableCell>
                  <TableCell className={`text-right font-mono ${(estimate.total || 0) >= 0 ? "" : "text-red-600"}`}>
                    {formatCurrency(estimate.total || 0)}
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8" 
                          onClick={() => openEditDialog(estimate, index)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Estimate?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will remove this estimate line item.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDelete(index)} 
                                className="bg-destructive text-destructive-foreground"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              <TableRow className="bg-muted/30 font-bold">
                <TableCell colSpan={2}>Base {title.replace(" Estimate", "")} Total</TableCell>
                <TableCell className={`text-right font-mono ${regularTotal >= 0 ? "" : "text-red-600"}`}>
                  {formatCurrency(regularTotal)}
                </TableCell>
                {canManage && <TableCell></TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <span className="text-muted-foreground">No estimates added</span>
            <span className="text-xl font-bold text-foreground">{formatCurrency(0)}</span>
          </div>
        )}
        
        {/* Change Order Adjustments */}
        {changeOrderEstimates.length > 0 && (
          <div className="space-y-2 mt-4">
            <p className="text-sm font-medium text-muted-foreground">Change Order Adjustments</p>
            {changeOrderEstimates.map((item, idx) => (
              <div key={item[idField] || item.id || `co-${idx}`} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <span className="text-blue-800 font-medium">
                    {item[nameField] || item.name}
                  </span>
                  {item.description && <p className="text-xs text-blue-600">{item.description}</p>}
                </div>
                <span className={`font-bold ${(item.total || 0) >= 0 ? "text-blue-700" : "text-red-600"}`}>
                  {formatCurrency(item.total)}
                </span>
              </div>
            ))}
          </div>
        )}
        
        {/* Total */}
        {(regularEstimates.length > 0 || changeOrderEstimates.length > 0) && (
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200 mt-4">
            <span className="text-green-800 font-medium">Total {title}</span>
            <span className={`text-xl font-bold ${totalEstimate >= 0 ? "text-green-700" : "text-red-600"}`}>
              {formatCurrency(totalEstimate)}
            </span>
          </div>
        )}
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingIndex !== null ? "Edit" : "Add"} {title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="form-group">
              <Label>Description *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Base Bid, Alternate 1, Deduct Alternate"
                autoFocus
                data-testid={`${testIdPrefix}-name-input`}
              />
            </div>
            <div className="form-group">
              <Label>Amount ($) *</Label>
              <NumericInput
                decimals={2}
                allowNegative={true}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00 (use negative for deductions)"
                data-testid={`${testIdPrefix}-amount-input`}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use negative values for deduct alternates
              </p>
            </div>
            <div className="form-group">
              <Label>Notes</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Additional notes about this estimate..."
                rows={2}
                data-testid={`${testIdPrefix}-description-input`}
              />
            </div>
            <Button onClick={handleSave} className="w-full" data-testid={`save-${testIdPrefix}-btn`}>
              {editingIndex !== null ? "Save Changes" : "Add Estimate"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default EstimateCard;
